import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { useVendor } from '../context/VendorContext';

const VendorRegistrationPage = () => {
  const navigate = useNavigate();
  const { refreshVendorCounts } = useVendor();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    location: '',
    description: '',
    phone: '',
    email: '',
    website: '',
    businessImage: null,
    experience: '',
    isSponsored: false,
    agreeToTerms: false
  });

  const [errors, setErrors] = useState({});

  // Function to validate image size (5MB limit)
  const validateImageSize = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    return file.size <= maxSize;
  };

  // 14 vendor categories matching your updated list
  const categories = [
    { value: 'catering-services', label: 'Catering Services' },
    { value: 'decorators', label: 'Decorators' },
    { value: 'bands-banjo', label: 'Bands & Banjo' },
    { value: 'photographers-content-creators', label: 'Photographers & Content Creators' },
    { value: 'makeup-artists', label: 'Makeup Artists' },
    { value: 'venues', label: 'Venues' },
    { value: 'dj-music', label: 'DJ & Music' },
    { value: 'invitation-cards', label: 'Invitation Cards' },
    { value: 'function-wear', label: 'Function Wear' },
    { value: 'stage-entrance-decorations', label: 'Stage & Entrance Decorations' },
    { value: 'cakes-gift-shops', label: 'Cakes & Gift Shops' },
    { value: 'anchors-hosts', label: 'Anchors & Hosts' },
    { value: 'entertainment-extras', label: 'Entertainment & Extras' },
    { value: 'sweets-snacks', label: 'Sweets & Snacks' }
  ];

  // Experience options for dropdown
  const experienceOptions = [
    { value: '1', label: '1 Year' },
    { value: '2', label: '2 Years' },
    { value: '3', label: '3 Years' },
    { value: '4', label: '4 Years' },
    { value: '5+', label: '5+ Years' },
    { value: '10+', label: '10+ Years' },
    { value: '20+', label: '20+ Years' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    // Special handling for phone number - only allow digits and limit to 10 digits
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length <= 10) {
        setFormData(prev => ({
          ...prev,
          [name]: digitsOnly
        }));
      }
    } else if (type === 'file') {
      // Handle business image upload with size validation
      if (name === 'businessImage' && files[0]) {
        const file = files[0];
        if (!validateImageSize(file)) {
          setErrors(prev => ({
            ...prev,
            [name]: 'Image size must be less than 5MB'
          }));
          return;
        }
        setFormData(prev => ({
          ...prev,
          [name]: file
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Business name is required';
    if (!formData.category) newErrors.category = 'Please select a category';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (formData.phone.length !== 10) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.experience.trim()) newErrors.experience = 'Experience is required';
    if (!formData.businessImage) newErrors.businessImage = 'Business image is required';
    if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      // Create FormData for file uploads
      const formDataToSend = new FormData();
      
      // Add text fields
      formDataToSend.append('businessName', formData.name);
      formDataToSend.append('ownerName', formData.name); // Using same for now
      formDataToSend.append('description', formData.description);
      formDataToSend.append('category', formData.category);
      
      // Add location data as JSON string
      formDataToSend.append('location', JSON.stringify({
        city: formData.location,
        state: 'Maharashtra',
        address: formData.location
      }));
      
      // Add contact data as JSON string
      formDataToSend.append('contact', JSON.stringify({
        phone: formData.phone,
        email: formData.email || undefined,
        website: formData.website || undefined
      }));
      
      // Add business info as JSON string
      formDataToSend.append('businessInfo', JSON.stringify({
        experience: formData.experience,
        established: new Date(),
        teamSize: 1
      }));
      
      // Add subscription info as JSON string
      formDataToSend.append('subscription', JSON.stringify({
        isSponsored: formData.isSponsored
      }));
      
      // Add pricing info as JSON string
      formDataToSend.append('pricing', JSON.stringify({
        budget: 'mid-range'
      }));
      
      // Add business image file
      if (formData.businessImage) {
        formDataToSend.append('businessImage', formData.businessImage);
      }

      // Send to API using public registration endpoint with multipart/form-data
      const response = await apiService.api.post('/vendors/register', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        // Refresh vendor counts on homepage
        refreshVendorCounts();
        
        setShowSuccess(true);
        setTimeout(() => {
          navigate(`/category/${formData.category}`);
        }, 3000);
      } else {
        throw new Error(response.data.message || 'Failed to add vendor');
      }
      
    } catch (error) {
      console.error('Error adding vendor:', error);
      const errorMessage = error.message || 'There was an error submitting your registration. Please try again.';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#FAF9F6'}}>
        <div className="max-w-md w-full mx-4">
          <div className="text-center p-8 rounded-lg shadow-lg" style={{backgroundColor: '#FFFFFF'}}>
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center bg-blue-600">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold mb-4 text-gray-900">
              Registration Successful! 🎉
            </h2>
            
            <p className="mb-6" style={{color: '#800000'}}>
              Welcome to Ceremonzy! Your business has been successfully added to our platform. 
              You'll be redirected to your category page shortly.
            </p>
            
            <div className="space-y-3 text-sm" style={{color: '#800000'}}>
              <p>✅ Business profile created</p>
              <p>✅ Added to {categories.find(c => c.value === formData.category)?.label}</p>
              <p>✅ Ready to receive customer inquiries</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" style={{backgroundColor: '#FAF9F6'}}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-gray-900">
            Join Ceremonzy
          </h1>
          <p className="text-xl text-gray-600">
            Register your ceremony business and connect with people planning their special day
          </p>
        </div>

        {/* Registration Form */}
        <div className="rounded-lg shadow-xl p-8" style={{backgroundColor: '#FFFFFF'}}>
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Basic Information */}
            <div className="border-b pb-6" style={{borderColor: '#FFD700'}}>
              <h3 className="text-xl font-semibold mb-4" style={{color: '#800000'}}>
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#800000'}}>
                    Business Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{
                      borderColor: errors.name ? '#EF4444' : '#D1D5DB',
                      focusRingColor: '#FFD700'
                    }}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)'}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                    placeholder="Enter your business name"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#800000'}}>
                    Category *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{
                      borderColor: errors.category ? '#EF4444' : '#D1D5DB',
                      color: '#800000'
                    }}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)'}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                  >
                    <option value="">Select your business category</option>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                  {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#800000'}}>
                    Location *
                  </label>
                  <select
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{
                      borderColor: errors.location ? '#EF4444' : '#D1D5DB',
                      color: '#800000'
                    }}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)'}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                  >
                    <option value="">Select your location</option>
                    <option value="Virar">Virar</option>
                    <option value="Vasai">Vasai</option>
                    <option value="Nalasopara">Nalasopara</option>
                    <option value="Others">Others</option>
                  </select>
                  {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#800000'}}>
                    Experience *
                  </label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{
                      borderColor: errors.experience ? '#EF4444' : '#D1D5DB',
                      color: '#800000'
                    }}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)'}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                  >
                    <option value="">Select your experience</option>
                    {experienceOptions.map(exp => (
                      <option key={exp.value} value={exp.value}>{exp.label}</option>
                    ))}
                  </select>
                  {errors.experience && <p className="text-red-500 text-sm mt-1">{errors.experience}</p>}
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium mb-2" style={{color: '#800000'}}>
                  Business Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none"
                  style={{borderColor: errors.description ? '#EF4444' : '#D1D5DB'}}
                  onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)'}
                  onBlur={(e) => e.target.style.boxShadow = 'none'}
                  placeholder="Describe your business, what makes you unique..."
                />
                {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-b pb-6" style={{borderColor: '#FFD700'}}>
              <h3 className="text-xl font-semibold mb-4" style={{color: '#800000'}}>
                Contact Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#800000'}}>
                    Phone Number * (10 digits only)
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{borderColor: errors.phone ? '#EF4444' : '#D1D5DB'}}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)'}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                    placeholder="9876543210"
                    maxLength="10"
                    pattern="[0-9]{10}"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  <p className="text-xs text-gray-500 mt-1">Enter 10-digit mobile number without country code</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#800000'}}>
                    Email Address (Optional)
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{borderColor: errors.email ? '#EF4444' : '#D1D5DB'}}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)'}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                    placeholder="your@email.com"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2" style={{color: '#800000'}}>
                    Website (Optional)
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{borderColor: '#D1D5DB'}}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)'}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>
            </div>

            {/* Business Image */}
            <div className="border-b pb-6" style={{borderColor: '#FFD700'}}>
              <h3 className="text-xl font-semibold mb-4" style={{color: '#800000'}}>
                Business Image
              </h3>

              {/* Important Image Ratio Note */}
              <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-blue-800 mb-2">
                      📐 Important: Image Ratio Guidelines
                    </h4>
                    <div className="text-sm text-blue-700 space-y-1">
                      <p>• <strong>Upload your business image in 4:3 aspect ratio</strong> to match our category listing layout</p>
                      <p>• <strong>Recommended sizes:</strong> 1200×900px, 800×600px, or 400×300px</p>
                      <p>• <strong>File formats:</strong> JPG, PNG, WebP only</p>
                      <p>• <strong>Maximum size:</strong> 5MB per image</p>
                      <p>• <strong>Quality tip:</strong> Use high-resolution images for crisp display across all devices</p>
                    </div>
                    <div className="mt-2 p-2 bg-blue-100 rounded text-xs text-blue-800">
                      <strong>Preview:</strong> Your image will be automatically resized to 400×300px for consistent display in category listings.
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{color: '#800000'}}>
                  Business Image <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    name="businessImage"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleChange}
                    className="hidden"
                    id="businessImage"
                  />
                  <label htmlFor="businessImage" className="cursor-pointer">
                    <div className="space-y-2">
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium text-blue-600 hover:text-blue-500">Upload Business Image</span>
                      </div>
                      <p className="text-xs text-gray-500">Main business photo - JPG, PNG, WebP (Max 5MB)</p>
                      <p className="text-xs text-gray-400">Will be optimized to 4:3 ratio for display</p>
                    </div>
                  </label>
                  {formData.businessImage && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-800">
                            📄 {formData.businessImage.name}
                          </p>
                          <p className="text-xs text-gray-600">
                            Size: {(formData.businessImage.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Will be optimized to 4:3 ratio (400×300px) for display
                          </p>
                        </div>
                        {formData.businessImage.size > 5 * 1024 * 1024 && (
                          <div className="ml-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              ⚠️ Exceeds 5MB limit
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {errors.businessImage && <p className="text-red-500 text-sm mt-1">{errors.businessImage}</p>}
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="border-b pb-6" style={{borderColor: '#FFD700'}}>
              <div className="flex items-start">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  className="mt-1 mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label className="text-sm" style={{color: '#800000'}}>
                  I agree to the Terms of Service and Privacy Policy *
                </label>
              </div>
              {errors.agreeToTerms && <p className="text-red-500 text-sm mt-1">{errors.agreeToTerms}</p>}
            </div>

            {/* Submit Button */}
            <div className="text-center pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: isSubmitting 
                    ? '#6B7280' 
                    : 'linear-gradient(135deg, #800000 0%, #8B0000 100%)',
                  color: '#FFD700'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.background = 'linear-gradient(135deg, #FFD700 0%, #FFFFFF 100%)';
                    e.target.style.color = '#800000';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.target.style.background = 'linear-gradient(135deg, #800000 0%, #8B0000 100%)';
                    e.target.style.color = '#FFD700';
                  }
                }}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Registering...
                  </div>
                ) : (
                  'Register My Business'
                )}
              </button>

            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default VendorRegistrationPage;